import React, { useState, useEffect, useRef } from 'react';
import type { CalendarProps } from '../../../types/dateTypes';
import { dateUtils } from '../../../utils/dateUtils';
import MonthYearSelector from './MonthYearSelector';

const Calendar: React.FC<CalendarProps> = ({
  selectedDate,
  onDateSelect,
  validationRules,
  onClose,
  isVisible
}) => {
  const calendarRef = useRef<HTMLDivElement>(null);
  const today = dateUtils.getToday();
  
  const [currentMonth, setCurrentMonth] = useState(
    selectedDate ? selectedDate.getMonth() : today.getMonth()
  );
  const [currentYear, setCurrentYear] = useState(
    selectedDate ? selectedDate.getFullYear() : validationRules?.minYear || today.getFullYear()
  );

  const dayNames = dateUtils.getShortDayNames();
  const calendarDays = dateUtils.getCalendarDays(
    currentYear,
    currentMonth,
    selectedDate,
    validationRules
  );

  // Handle click outside to close calendar
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      
      // Only close if the click is truly outside the calendar container
      if (calendarRef.current && target && !calendarRef.current.contains(target)) {
        // Double-check: make sure it's not a calendar-related element
        const isCalendarElement = target.closest('.calendar-dropdown') ||
                                 target.closest('.calendar-container') ||
                                 target.closest('.date-picker-wrapper') ||
                                 target.classList.contains('calendar-dropdown') ||
                                 target.classList.contains('calendar-container');
        
        if (!isCalendarElement) {
          onClose();
        }
      }
    };

    if (isVisible) {
      // Use capture phase to handle the event before it bubbles
      document.addEventListener('mousedown', handleClickOutside, true);
      return () => document.removeEventListener('mousedown', handleClickOutside, true);
    }
  }, [isVisible, onClose]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isVisible) return;

      switch (event.key) {
        case 'Escape':
          event.preventDefault();
          onClose();
          break;
        case 'Enter':
          event.preventDefault();
          if (selectedDate && !dateUtils.isDateDisabled(selectedDate, validationRules)) {
            onDateSelect(selectedDate);
          }
          break;
      }
    };

    if (isVisible) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isVisible, selectedDate, validationRules, onClose, onDateSelect]);

  const handleDateClick = (date: Date) => {
    if (!dateUtils.isDateDisabled(date, validationRules)) {
      onDateSelect(date);
    }
  };

  const handleApply = () => {
    if (selectedDate && !dateUtils.isDateDisabled(selectedDate, validationRules)) {
      onClose();
    }
  };

  const handleCancel = () => {
    onClose();
  };

  // Prevent calendar from closing when clicking anywhere within the calendar
  const handleCalendarClick = (event: React.MouseEvent) => {
    // Stop event propagation and prevent default to ensure no unwanted closures
    event.stopPropagation();
    event.preventDefault();
  };

  // Also handle mousedown events to be extra sure
  const handleCalendarMouseDown = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  if (!isVisible) return null;

  return (
    <div
      className="calendar-container"
      ref={calendarRef}
      onClick={handleCalendarClick}
      onMouseDown={handleCalendarMouseDown}
    >
      <div className="calendar-header">
        <MonthYearSelector
          currentMonth={currentMonth}
          currentYear={currentYear}
          onMonthChange={setCurrentMonth}
          onYearChange={setCurrentYear}
          minYear={validationRules?.minYear || currentYear - 10}
          maxYear={validationRules?.maxYear || currentYear + 10}
        />
      </div>

      <div className="calendar-grid">
        <div className="day-headers">
          {dayNames.map((day) => (
            <div key={day} className="day-header">
              {day}
            </div>
          ))}
        </div>

        <div className="days-grid">
          {calendarDays.map((calendarDay, index) => {
            const { date, isCurrentMonth, isToday, isSelected, isDisabled } = calendarDay;
            
            return (
              <button
                key={index}
                type="button"
                className={`
                  calendar-day
                  ${isCurrentMonth ? 'current-month' : 'other-month'}
                  ${isToday ? 'today' : ''}
                  ${isSelected ? 'selected' : ''}
                  ${isDisabled ? 'disabled' : ''}
                `.trim()}
                onClick={() => handleDateClick(date)}
                disabled={isDisabled}
                aria-label={`${dateUtils.formatDate(date)} ${isToday ? '(Today)' : ''} ${isSelected ? '(Selected)' : ''}`}
              >
                {date.getDate()}
              </button>
            );
          })}
        </div>
      </div>

      <div className="calendar-actions">
        <button
          type="button"
          className="calendar-button cancel-button"
          onClick={handleCancel}
        >
          Cancel
        </button>
        <button
          type="button"
          className="calendar-button apply-button"
          onClick={handleApply}
          disabled={!selectedDate || dateUtils.isDateDisabled(selectedDate, validationRules)}
        >
          Select
        </button>
      </div>
    </div>
  );
};

export default Calendar;